import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const METHODS = [
  ['card', 'Pay with Card', 'Use your debit/credit card'],
  ['banktransfer', 'Pay with Transfer', 'Transfer from your bank app'],
  ['account', 'Pay with Bank', 'Bank account payment'],
  ['ussd', 'Pay with USSD', 'Dial and pay instantly'],
  ['opay', 'Pay with OPay', 'Use your OPay wallet'],
];

const EMPTY_MINIMUMS = {
  card: null,
  banktransfer: null,
  account: null,
  ussd: null,
  opay: null,
};

export default function DepositPage() {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('card');
  const [minimums, setMinimums] = useState(EMPTY_MINIMUMS);
  const [configError, setConfigError] = useState('');
  const [configLoading, setConfigLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    api.wallet.getWallet()
      .then(res => {
        if (!mounted) return;
        const data = res.data?.data || res.data || {};
        const globalMinimum = Number(data.minDeposit);
        const configured = data.depositMinimums && typeof data.depositMinimums === 'object'
          ? data.depositMinimums
          : null;
        if (configured) {
          setMinimums({
            card: Number(configured.card) || globalMinimum,
            banktransfer: Number(configured.banktransfer) || globalMinimum,
            account: Number(configured.account) || Math.max(globalMinimum, 200),
            ussd: Number(configured.ussd) || globalMinimum,
            opay: Number(configured.opay) || globalMinimum,
          });
        } else if (Number.isFinite(globalMinimum) && globalMinimum > 0) {
          setMinimums({
            card: globalMinimum,
            banktransfer: globalMinimum,
            account: Math.max(globalMinimum, 200),
            ussd: globalMinimum,
            opay: globalMinimum,
          });
        }
      })
      .catch(() => {
        if (mounted) setConfigError('Deposit limits could not be loaded. Please try again.');
      })
      .finally(() => {
        if (mounted) setConfigLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const selectedMinimum = minimums[method] == null ? NaN : Number(minimums[method]);
  const numericAmount = Number(amount);
  const canProceed = !configLoading && Number.isFinite(selectedMinimum) && Number.isFinite(numericAmount) && numericAmount > 0 && numericAmount >= selectedMinimum && !busy;

  const minimumMessage = useMemo(() => {
    if (amount === '') return '';
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) return 'Enter a valid deposit amount.';
    if (numericAmount < selectedMinimum) return `Minimum deposit is ₦${selectedMinimum.toLocaleString()}.`;
    return '';
  }, [amount, numericAmount, selectedMinimum]);

  const selectMethod = nextMethod => {
    setMethod(nextMethod);
    setError('');
  };

  const pay = async () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) {
      return setError('Enter a valid deposit amount.');
    }
    if (n < selectedMinimum) {
      return setError(`Minimum deposit is ₦${selectedMinimum.toLocaleString()}.`);
    }

    setBusy(true);
    setError('');
    try {
      const requestId = 'dep' + Date.now() + Math.random().toString(36).slice(2);
      const r = await api.wallet.deposit({ amount: n, paymentMethod: method, requestId });
      const data = r.data.data || r.data;
      if (data.flow === 'mono' && data.redirect_url) return window.location.assign(data.redirect_url);
      if (!data.public_key || !data.reference) throw new Error('Payment checkout could not be initialized.');

      if (!window.FlutterwaveCheckout) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.flutterwave.com/v3.js';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Checkout unavailable'));
          document.head.appendChild(script);
        });
      }

      window.FlutterwaveCheckout({
        public_key: data.public_key,
        tx_ref: data.reference,
        amount: data.amount,
        currency: data.currency,
        payment_options: data.payment_method,
        enforce_payment_options: true,
        redirect_url: '/wallet/deposit/confirm?tx_ref=' + encodeURIComponent(data.reference),
        customer: data.customer,
        customizations: { title: '9jaWin Wallet Deposit', description: 'Fund your 9jaWin wallet' },
      });
    } catch (e) {
      const providerMinimum = Number(e.response?.data?.minimum);
      if (e.response?.data?.code === 'DEPOSIT_BELOW_MINIMUM' && Number.isFinite(providerMinimum)) {
        setMinimums(prev => ({ ...prev, [method]: providerMinimum }));
        setError(`Minimum deposit is ₦${providerMinimum.toLocaleString()}.`);
      } else {
        setError(e.response?.data?.message || e.message || 'Deposit failed.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main style={p.page}>
      <header style={p.header}>
        <Link to="/wallet" style={p.back}>←</Link>
        <h1>Deposit</h1>
      </header>

      <div style={p.label}>You pay</div>
      <input
        style={p.input}
        inputMode="decimal"
        value={amount}
        onChange={e => {
          setAmount(e.target.value.replace(/[^0-9.]/g, ''));
          setError('');
        }}
        placeholder="₦0"
        aria-label="Deposit amount"
      />

      <div style={p.quick}>
        {[100, 500, 1000, 3000].map(n => (
          <button
            type="button"
            key={n}
            disabled={configLoading || !Number.isFinite(selectedMinimum) || n < selectedMinimum || busy}
            onClick={() => {
              setAmount(String(n));
              setError('');
            }}
            style={{ ...p.quickButton, opacity: configLoading || !Number.isFinite(selectedMinimum) || n < selectedMinimum || busy ? 0.45 : 1 }}
          >
            +₦{n.toLocaleString()}
          </button>
        ))}
      </div>

      <h3>Payment methods</h3>
      {METHODS.map(([key, title, description]) => (
        <button
          type="button"
          key={key}
          onClick={() => selectMethod(key)}
          style={{ ...p.method, ...(method === key ? p.selected : {}) }}
        >
          <b>{title}</b>
          <small>{description}</small>
        </button>
      ))}

      <div style={p.minimum}>{Number.isFinite(selectedMinimum) ? `Minimum deposit: ₦${selectedMinimum.toLocaleString()}` : (configError || 'Loading deposit limit…')}</div>
      {minimumMessage && <div role="alert" style={p.error}>{minimumMessage}</div>}
      {error && error !== minimumMessage && <div role="alert" style={p.error}>{error}</div>}

      <div style={p.final}>
        <span>Final amount</span>
        <b>₦{Number(amount || 0).toLocaleString()}</b>
      </div>

      <button
        type="button"
        disabled={!canProceed}
        onClick={pay}
        style={{ ...p.primary, opacity: canProceed ? 1 : 0.5, cursor: canProceed ? 'pointer' : 'not-allowed' }}
      >
        {busy ? 'Processing…' : 'Proceed to payment'}
      </button>

      <small style={p.secure}>🛡 Secure payments. Wallet credits only after provider verification.</small>
    </main>
  );
}

const p = {
  page: { maxWidth: 560, width: '100%', margin: '0 auto', padding: '18px 16px 40px', color: '#fff' },
  header: { display: 'flex', gap: 14, alignItems: 'center', marginBottom: 24 },
  back: { color: '#aaa', fontSize: 24, textDecoration: 'none' },
  label: { color: '#999', fontSize: 13 },
  input: { width: '100%', boxSizing: 'border-box', background: '#171717', border: '1px solid #303030', borderRadius: 12, color: '#fff', fontSize: 32, fontWeight: 800, padding: 18, margin: '8px 0' },
  quick: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 7, marginBottom: 28 },
  quickButton: { padding: '9px 5px', borderRadius: 9, border: '1px solid #303030', background: '#171717', color: '#fff', fontWeight: 700, cursor: 'pointer' },
  method: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', padding: 15, marginBottom: 8, borderRadius: 12, border: '1px solid #292929', background: '#171717', color: '#fff', textAlign: 'left', cursor: 'pointer' },
  selected: { borderColor: '#00d4aa', background: '#10241f' },
  minimum: { marginTop: 10, color: '#aaa', fontSize: 12 },
  error: { margin: '10px 0', padding: 12, borderRadius: 8, background: '#351818', color: '#ff9999' },
  final: { display: 'flex', justifyContent: 'space-between', padding: '18px 0', marginTop: 14, borderTop: '1px solid #292929' },
  primary: { width: '100%', padding: 15, border: 0, borderRadius: 11, background: '#00d4aa', fontWeight: 800 },
  secure: { display: 'block', textAlign: 'center', color: '#777', marginTop: 12 },
};
